/**
 * Container Widget - Aurelio Builder
 * 
 * @description Widget modular para contenedores flexibles que pueden albergar otros elementos.
 * Soporta layout vertical/horizontal, propiedades responsive y anidación recursiva.
 */

import React from 'react';
import { FiGrid } from 'react-icons/fi';
import { ELEMENT_TYPES, WIDGET_CATEGORIES } from '../../../../constants/elementTypes.js';
import { VIEWPORT_MODES } from '../../../../constants/viewportConfigs.js';
import { WidgetRegistry } from '../../registry/index.js';

/**
 * Propiedades por defecto del Container Widget
 */
export const ContainerDefaultProps = {
  layout: 'vertical', // vertical, horizontal
  gap: '16px',
  padding: '20px',
  backgroundColor: 'transparent',
  borderRadius: '0px',
  border: 'none',
  minHeight: '100px',
  alignment: 'left', // left, center, right
  children: [], // Array de elementos hijos
  
  // Propiedades responsives - cada viewport tiene sus propias propiedades
  responsive: {
    [VIEWPORT_MODES.DESKTOP]: {
      flexDirection: 'row', // Horizontal en escritorio
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'nowrap',
      gap: '24px',
      padding: '32px',
    },
    [VIEWPORT_MODES.TABLET]: {
      flexDirection: 'column', // Vertical en tablet
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      flexWrap: 'nowrap',
      gap: '20px',
      padding: '24px',
    },
    [VIEWPORT_MODES.MOBILE]: {
      flexDirection: 'column', // Vertical en móvil
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      flexWrap: 'nowrap',
      gap: '16px',
      padding: '16px',
    }
  },
  
  // Propiedades legacy para compatibilidad
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  flexWrap: 'nowrap',
  width: '1340px',
  height: '200px',
  widthType: 'full', // 'full' o 'box'
  columnGap: '20px',
  rowGap: '20px',
};

/**
 * Componente de renderizado del Container
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.element - Datos del elemento container
 * @param {Function} props.onSelect - Callback al seleccionar elemento
 * @param {Function} props.onDelete - Callback al eliminar elemento
 * @param {Function} props.onDuplicate - Callback al duplicar elemento
 * @param {boolean} props.isSelected - Si el elemento está seleccionado
 * @param {Function} props.onAddToContainer - Callback para agregar elementos al container
 * @param {Function} props.onMoveToContainer - Callback para mover elementos al container
 * @param {Object} props.selectedElement - Elemento actualmente seleccionado
 * @param {string} props.viewportMode - Modo de viewport actual
 * @param {Object} props.parentElement - Elemento padre (si existe)
 * @returns {React.Component} Componente Container renderizado
 */
export const ContainerRenderer = ({ 
  element, 
  onSelect, 
  onDelete, 
  onDuplicate, 
  isSelected, 
  onAddToContainer, 
  onMoveToContainer, 
  selectedElement, 
  viewportMode, 
  parentElement 
}) => {
  // Este componente será implementado en el siguiente paso
  // Por ahora retornamos un placeholder para no romper la funcionalidad
  return (
    <div 
      onClick={() => onSelect(element)}
      className={`border-2 border-dashed ${isSelected ? 'border-blue-500' : 'border-gray-300'} p-4 rounded`}
    >
      <p className="text-sm text-gray-500">Container Widget (Modularized)</p>
      <p className="text-xs text-gray-400">ID: {element.id}</p>
    </div>
  );
};

/**
 * Componente de propiedades de layout del Container
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.element - Elemento seleccionado
 * @param {Function} props.onUpdate - Callback para actualizar elemento
 * @param {string} props.viewportMode - Modo de viewport actual
 * @returns {React.Component} Controles de layout del container
 */
export const ContainerLayoutProperties = ({ element, onUpdate, viewportMode }) => {
  // Placeholder para propiedades de layout
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-300">Container Layout Properties</p>
      <p className="text-xs text-gray-400">Viewport: {viewportMode}</p>
    </div>
  );
};

/**
 * Componente de propiedades de estilo del Container
 * 
 * @param {Object} props - Propiedades del componente  
 * @param {Object} props.element - Elemento seleccionado
 * @param {Function} props.onUpdate - Callback para actualizar elemento
 * @param {string} props.viewportMode - Modo de viewport actual
 * @returns {React.Component} Controles de estilo del container
 */
export const ContainerStyleProperties = ({ element, onUpdate, viewportMode }) => {
  // Placeholder para propiedades de estilo
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-300">Container Style Properties</p>
      <p className="text-xs text-gray-400">Background, borders, etc.</p>
    </div>
  );
};

/**
 * Configuración completa del Container Widget
 */
export const ContainerWidget = {
  type: ELEMENT_TYPES.CONTAINER,
  name: 'Contenedor',
  icon: FiGrid,
  category: WIDGET_CATEGORIES.LAYOUT,
  defaultProps: ContainerDefaultProps,
  
  // Componentes del widget
  Renderer: ContainerRenderer,
  Properties: {
    Layout: ContainerLayoutProperties,
    Style: ContainerStyleProperties
  },
  
  // Metadatos adicionales
  description: 'Contenedor flexible que puede albergar otros elementos con soporte para layout responsive',
  tags: ['layout', 'container', 'responsive', 'flexbox'],
  version: '1.0.0'
};

// Auto-registrar el widget al importar este archivo
WidgetRegistry.register(ContainerWidget);

console.log('📦 Container widget loaded and registered');

export default ContainerWidget;
